'use client';

import React from 'react';
import {useI18n} from '@udemy-v2/i18n';
import {ClpPrimaryCtaButton} from '@/features/course-landing-page/components/clp-primary-cta-button/clp-primary-cta-button.react-component';
import {GwgEnrollmentCTA} from '@udemy-v2/career-accelerators';
import {GwgSubscriptionCTA, GwgCheckoutProvider} from '@udemy-v2/react-subscription-browse';
import {useGwgPlanPreference} from '@/features/course-landing-page/hooks/use-gwg-plan-preference';
import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {useCourseCareerAccelerator} from '@/features/course-landing-page/context/career-accelerator-provider.react-component';
import {useClpSubscriptionContext} from '@/features/course-landing-page/context/clp-subscription-context.react-component';
import {useClpPurchaseContext} from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import {SubscriptionPlanSubscriberState} from '@udemy-v2/subscription-graphql';
import styles from './grow-with-google-sticky-footer-content.module.scss';

/**
 * GrowWithGoogleStickyFooterContent component displays the CTA button and billing information
 * for Grow with Google subscription enrollment in the sticky footer.
 * Renders when the course is included in the Grow with Google subscription.
 */
export const GrowWithGoogleStickyFooterContent: React.FC = () => {
    const {gettext} = useI18n();
    const course = useClpCourse();
    const {subscribedConsumerSubscriptionPlan} = useClpSubscriptionContext();
    const {
        gwgPlan,
        gwgPlusPersonalPlan,
        isFeaturesLoading,
        isMonthlyPersonalPlanAvailable,
    } = useClpPurchaseContext();
    const courseCareerAccelerator = useCourseCareerAccelerator();
    const gwgPlanPreference = useGwgPlanPreference();

    // When gwgPlan is still loading but the combined plan has resolved, pass undefined
    // so GwgSubscriptionCTA can render the upgrade CTA without showing a loading skeleton.
    const plan =
        gwgPlan?.isLoading && gwgPlusPersonalPlan?.isLoading === false ? undefined : gwgPlan;

    const isGwgSubscriber =
        (gwgPlan && 'isSubscriber' in gwgPlan && gwgPlan?.isSubscriber) ||
        (gwgPlusPersonalPlan &&
            'isSubscriber' in gwgPlusPersonalPlan &&
            gwgPlusPersonalPlan?.isSubscriber);

    let content = (
        <GwgCheckoutProvider planPreference={gwgPlanPreference}>
            <GwgSubscriptionCTA
                plan={plan}
                currentlySubscribedPlan={
                    subscribedConsumerSubscriptionPlan &&
                    subscribedConsumerSubscriptionPlan.subscribedPlan.productType
                        ? ({
                              isSubscriber: true,
                              isLoading: false,
                              productType:
                                  subscribedConsumerSubscriptionPlan.subscribedPlan.productType,
                          } as SubscriptionPlanSubscriberState)
                        : undefined
                }
                combinedGwgPlan={gwgPlusPersonalPlan}
                courseId={parseInt(course.id, 10)}
                courseTrackingId={course.trackingId}
                uiRegion="sticky_footer"
                offerCtaStyle="primary"
                className={styles['cta-button']}
                subtitleClassName={styles['billing-text']}
                planPeriodText={
                    isFeaturesLoading
                        ? undefined
                        : isMonthlyPersonalPlanAvailable
                          ? gettext('Billed monthly or annually. Cancel anytime.')
                          : gettext('Cancel anytime.')
                }
            />
        </GwgCheckoutProvider>
    );

    if (isGwgSubscriber) {
        if (courseCareerAccelerator && !course.isEnrolled) {
            content = (
                <GwgEnrollmentCTA
                    udStyle="primary"
                    size="large"
                    className={styles['cta-button']}
                    acceleratorId={Number(courseCareerAccelerator.id)}
                    unEnrolledSubtitle={gettext(
                        'You will be enrolled in all courses included in this path.',
                    )}
                />
            );
        } else {
            content = <ClpPrimaryCtaButton className={styles['cta-button']} />;
        }
    }

    return (
        <div className={styles.container}>
            <div className={styles['cta-wrapper']}>{content}</div>
        </div>
    );
};
